/**
 * Row primitives shared by both Trade economics layouts (the single editable
 * card and the original two-card split — see ECONOMICS_LAYOUT in
 * AcquisitionsEconomicsTab.js).
 *
 *   StatRow   label left, read-only value right (a computed result)
 *   InputRow  label left, control right at half the width — the same row the
 *             Loan Info tab edits its fields in (FieldRow / EditControl)
 */
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import DollarInput from "../../../../components/inputs/DollarInput";
import PercentInput from "../../../../components/inputs/PercentInput";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import { slate, teal } from "../../../../theme/colors";

const ROW_DIVIDER = {
  borderBottom: `1px solid ${T.color.border}`,
  "&:last-of-type": { borderBottom: "none" },
};

const Hint = ({ text }) =>
  text ? (
    <Tooltip arrow placement="top" title={text}>
      <InfoOutlined sx={{ fontSize: 14, color: slate[300], cursor: "help" }} />
    </Tooltip>
  ) : null;

Hint.propTypes = { text: PropTypes.node };

export const StatRow = ({ label, value, strong, hint, pending }) => {
  const empty = value === null || value === undefined || value === "";
  return (
    <Flx
      jb
      ac
      sx={{ paddingBlock: "10px", gap: "16px", minHeight: 44, ...ROW_DIVIDER }}
    >
      <Flx ac sx={{ gap: "4px", minWidth: 0 }}>
        <Txt sx={{ fontSize: "12.5px", color: T.color.ink3 }}>{label}</Txt>
        <Hint text={hint} />
      </Flx>
      <Txt
        sx={{
          ...T.type.fieldValue,
          fontSize: "12.5px",
          fontWeight: strong ? 700 : 600,
          color: empty ? T.color.muted : strong ? T.color.ink : T.color.ink2,
          // A result the unsaved edits will change — shown, but quieter.
          fontStyle: pending ? "italic" : "normal",
        }}
      >
        {empty ? "—" : value}
      </Txt>
    </Flx>
  );
};

StatRow.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  strong: PropTypes.bool,
  hint: PropTypes.node,
  pending: PropTypes.bool,
};

export const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    background: "#fff",
    minHeight: 36,
    fontSize: "13px",
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-input": {
    py: "8px",
    px: "10px",
    fontSize: "13px",
    textAlign: "right",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: slate[200] },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: slate[300] },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: teal[500],
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: `0 0 0 3px ${teal[100]}`,
  },
};

/**
 * `field.kind`: "money" (DollarInput → number), "percent" (PercentInput —
 * shows 9.75%, the value is the decimal 0.0975), anything else a plain
 * numeric text box. `field.help` renders as an (i) tooltip on the label.
 */
export const InputRow = ({ field, value, onChange, edited }) => (
  <Flx
    ac
    jb
    sx={{ gap: "16px", minHeight: 48, paddingBlock: "6px", ...ROW_DIVIDER }}
  >
    <Flx ac sx={{ gap: "4px", minWidth: 0 }}>
      <Txt sx={{ fontSize: 13, color: slate[600] }}>{field.label}</Txt>
      <Hint text={field.help} />
      {edited && (
        <Box
          aria-label="Changed"
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: teal[500],
            ml: "2px",
          }}
        />
      )}
    </Flx>
    <Box sx={{ flex: "0 1 50%", minWidth: 0 }}>
      {field.kind === "money" || field.money ? (
        <DollarInput
          value={value ?? ""}
          onChange={(v) => {
            if (typeof v === "number" || v == null) onChange(v);
          }}
          size="small"
          placeholder=""
          fullWidth
          sx={inputSx}
        />
      ) : field.kind === "percent" ? (
        <PercentInput
          value={value ?? ""}
          onChange={(v) => onChange(v ?? null)}
          size="small"
          placeholder=""
          sx={inputSx}
        />
      ) : (
        <TextField
          size="small"
          fullWidth
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ "aria-label": field.label, inputMode: "decimal" }}
          sx={inputSx}
        />
      )}
    </Box>
  </Flx>
);

InputRow.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.node,
    help: PropTypes.node,
    kind: PropTypes.string,
    money: PropTypes.bool,
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  edited: PropTypes.bool,
};
